import React, { useState } from 'react'
import { ScrollView, TouchableOpacity, View } from 'react-native'
import { Controller, useForm } from 'react-hook-form'
import { useTheme } from '@react-navigation/native'
import { yupResolver } from '@hookform/resolvers/yup'
import { FullRegistrationData, Step1Data, usePaginateCities, usePaginateCountries, usePaginateLanguages } from '@hooks'
import { Button, ImageUpload, OnBoarding, Text, TextInput } from '@components'
import { useLanguage } from '@context'
import { FormSwitch, UserConsent } from '../components'
import { FormDate, GenericSelectionModal } from '../../../../components/organisms'
import { Step1ValidationSchema } from './schema'
import getStyle from './style'

export const Step1Form = ({
  onSubmit,
}: {
  onSubmit: (newData: Partial<FullRegistrationData>) => void
}) => {
  const { t, currentLanguage } = useLanguage()

  const theme = useTheme()
  const styles = getStyle(theme)

  const [countriesSearch, setCountriesSearch] = useState('')
  const [citiesSearch, setCitiesSearch] = useState('')
  const [languagesSearch, setLanguagesSearch] = useState('')
  const { flatData: allCountries, isFetching: isFetchingCountries, isFetchingNextPage: isFetchingNextPageCountries, fetchNextPage: fetchNextPageCountries, hasNextPage: hasNextPageCountries } = usePaginateCountries({ search: countriesSearch, lang: currentLanguage })
  const { flatData: allCities, isFetching: isFetchingCities, isFetchingNextPage: isFetchingNextPageCities, fetchNextPage: fetchNextPageCities, hasNextPage: hasNextPageCities } = usePaginateCities({ search: citiesSearch, lang: currentLanguage })
  const { flatData: allLanguages, isFetching: isFetchingLanguages, isFetchingNextPage: isFetchingNextPageLanguages, fetchNextPage: fetchNextPageLanguages, hasNextPage: hasNextPageLanguages } = usePaginateLanguages({ search: languagesSearch, lang: currentLanguage })

  const [isOriginModalVisible, setIsOriginModalVisible] = useState(false)
  const [isNationalityModalVisible, setIsNationalityModalVisible] = useState(false)
  const [isAddressModalVisible, setIsAddressModalVisible] = useState(false)
  const [isLanguagesModalVisible, setIsLanguagesModalVisible] = useState(false)

  const [isUserConsent, setIsUserConsent] = useState(false)

  const { control, handleSubmit, setValue, getValues } = useForm<Step1Data>({
    defaultValues: {
      gender: 'female',
    },
    resolver: yupResolver(Step1ValidationSchema),
  })
  return (
    <ScrollView
      style={styles.scrollviewContainer}
      contentContainerStyle={styles.scrollviewContentContainer}
    >
      <OnBoarding>
        <Controller
          control={control}
          name="avatar"
          render={({ field: { onChange }, fieldState: { error } }) => (
            <>
              <ImageUpload
                title={t('registerStep1.title.picture')}
                onImageSelect={(uri) => onChange(uri)}
              />
              {error?.message && (
                <Text style={styles.errorMessage} type="microText" color={theme.colors.error}>
                  {error.message}
                </Text>
              )}
            </>
          )}
        />

        <FormSwitch
          control={control}
          name="gender"
          title={t('registerStep1.title.gender')}
          left={{ label: t('registerStep1.value.women'), value: 'female' }}
          middle={{ label: t('registerStep1.value.man'), value: 'male' }}
          right={{ label: t('registerStep1.value.nonBinary'), value: 'non-binary' }}
        />

        <Controller
          control={control}
          name="firstName"
          render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
            <TextInput
              containerStyle={styles.textInputContainer}
              title={t('registerStep1.title.name')}
              iconLeft="user"
              onBlur={onBlur}
              onChangeText={onChange}
              value={value}
              //errorMessage={error?.message}
              hasError={!!error}
            />
          )}
        />

        <Controller
          control={control}
          name="lastName"
          render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
            <TextInput
              containerStyle={styles.textInputContainer}
              title={t('registerStep1.title.surname')}
              iconLeft="user"
              onBlur={onBlur}
              onChangeText={onChange}
              value={value}
              //errorMessage={error?.message}
              hasError={!!error}
            />
          )}
        />

        <View style={styles.textInputContainer}>
          <FormDate
            control={control}
            name="birthday"
            title={t('registerStep1.title.birthday')}
            icon="gift"
          />
        </View>

        <TouchableOpacity onPress={() => setIsAddressModalVisible(true)}>
          <Controller
            control={control}
            name="residence"
            render={({ field: { value }, fieldState: { error } }) => (
              <TextInput
                title={t('registerStep1.title.residence')}
                value={value?.name}
                placeholder={t('registerStep1.title.residence')}
                containerStyle={styles.textInputContainer}
                iconRight="globe"
                editable={false}
                pointerEvents="none"
                hasError={!!error}
              />
            )}
          />
        </TouchableOpacity>

        <TouchableOpacity onPress={() => setIsOriginModalVisible(true)}>
          <Controller
            control={control}
            name="origins"
            render={({ field: { value }, fieldState: { error } }) => (
              <TextInput
                title={t('registerStep1.title.origin')}
                value={(value || []).map(item => item.name).join(', ')}
                placeholder={t('registerStep1.title.origin')}
                containerStyle={styles.textInputContainer}
                iconRight="globe"
                editable={false}
                pointerEvents="none"
                hasError={!!error}
              />
            )}
          />
        </TouchableOpacity>

        <TouchableOpacity onPress={() => setIsNationalityModalVisible(true)}>
          <Controller
          control={control}
          name="nationalities"
          render={({ fieldState: { error } }) => (
            <TextInput
            title={t('registerStep1.title.nationality')}
              value={getValues('nationalities')?.map(nat => nat.name).join(', ')}
              placeholder={t('registerStep1.title.nationality')}
              containerStyle={styles.textInputContainer}
              iconRight="globe"
              editable={false}
              pointerEvents="none"
              hasError={!!error}
            />
          )}
        />
        </TouchableOpacity>
        
        <TouchableOpacity onPress={() => setIsLanguagesModalVisible(true)}>
          <Controller
          control={control}
          name="languages"
          render={({ field: { value }, fieldState: { error } }) => (
            <TextInput
            title={t('registerStep1.title.languages')}
            value={(value || [])?.map(lang => lang.name).join(', ')}
            placeholder={t('registerStep1.title.languages')}
            containerStyle={styles.textInputContainer}
            iconRight="bubbleRound"
            editable={false}
            pointerEvents="none"
            hasError={!!error}
            />
          )}
        />
        </TouchableOpacity>

        <Controller
          control={control}
          name="info"
          render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
            <TextInput
              containerStyle={styles.textInputContainer}
              title={t('registerStep1.title.info')}
              iconLeft="edit"
              onBlur={onBlur}
              onChangeText={onChange}
              value={value}
              //errorMessage={error?.message}
              hasError={!!error}
              isMultiline
              scrollEnabled={false}
            />
          )}
        />

        <UserConsent title={t('registerStep1.title.conditions')} value={isUserConsent} onChange={setIsUserConsent} />

        <Button
          disabled={!isUserConsent}
          title={t('registerStep1.next')}
          onPress={handleSubmit(onSubmit)}
          containerStyle={styles.button}
        />
      </OnBoarding>
      <GenericSelectionModal
        isVisible={isOriginModalVisible || isAddressModalVisible}
        setIsVisible={(val) => {
          if (isOriginModalVisible) {
            setIsOriginModalVisible(val)
          } else if (isAddressModalVisible) {
            setIsAddressModalVisible(val)
          }
        }}
        title={isOriginModalVisible ? t('form.origins') : t('form.livingPlace')}
        placeholder={t('form.searchPlace')}
        availableItems={isAddressModalVisible ? allCities.map(city => ({ id: city.id, name: city.name ?? city.nameFr })) : allCountries.map(country => ({ id: country.code, name: country.name ?? country.nameFr }))}
        selectedItems={
          isOriginModalVisible
            ? (getValues('origins') || [])
            : [getValues('residence')].filter((item) => Boolean(item))
        }
        onSelectionChange={(items) => {
          if (items.length === 0) return
          if (isOriginModalVisible) {
            setValue('origins', items, { shouldValidate: true, shouldDirty: true })
          } else if (isAddressModalVisible) {
            setValue('residence', items[0], { shouldValidate: true, shouldDirty: true })
          }
        }}
        multiSelect={isOriginModalVisible}
        isFetching={isFetchingCountries || isFetchingCities}
        fetchNextPage={() => {
          if (isOriginModalVisible) {
            fetchNextPageCountries()
          } else if (isAddressModalVisible) {
            fetchNextPageCities()
          }
        }}
        hasNextPage={isOriginModalVisible ? hasNextPageCountries : isAddressModalVisible ? hasNextPageCities : false}
        isFetchingNextPage={isOriginModalVisible ? isFetchingNextPageCountries : isFetchingNextPageCities}
        onSearch={(text) => {
          if (isOriginModalVisible) setCountriesSearch(text)
          else if (isAddressModalVisible) setCitiesSearch(text)
        }}
      />

      <GenericSelectionModal
        isVisible={isNationalityModalVisible}
        setIsVisible={(val) => setIsNationalityModalVisible(val)  }
        title={t('form.nationalities')}
        placeholder={t('form.searchNationality')}
        availableItems={allCountries.map(country => ({ id: country.code, name: country.name ?? country.nameFr }))}
        selectedItems={
          getValues('nationalities') || []
        }
        onSelectionChange={(items) => {
          setValue('nationalities', items, { shouldValidate: true, shouldDirty: true })
        }}
        isFetching={isFetchingCountries}
        fetchNextPage={() => {
          fetchNextPageCountries()
        }}
        hasNextPage={hasNextPageCountries}
        isFetchingNextPage={isFetchingNextPageCountries}
        onSearch={setCountriesSearch}
      />

      <GenericSelectionModal
        isVisible={isLanguagesModalVisible}
        setIsVisible={setIsLanguagesModalVisible}
        title={t('form.spokenLanguages')}
        placeholder={t('form.searchLanguage')}
        availableItems={allLanguages.map((language) => ({
          id: language.code,
          name: language.name ?? language.nameFr,
        }))}
        selectedItems={getValues('languages') || []}
        onSelectionChange={(items) => {
          setValue('languages', items, { shouldValidate: true, shouldDirty: true })
        }}
        isFetching={isFetchingLanguages}
        fetchNextPage={() => {
          fetchNextPageLanguages()
        }}
        hasNextPage={hasNextPageLanguages}
        isFetchingNextPage={isFetchingNextPageLanguages}
        onSearch={setLanguagesSearch}
      />
    </ScrollView>
  )
}
